import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { describeError } from '@/components/primitives';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Axios instance for the admin console. Adds the admin bearer token and, when
 * the server rejects the session (401, or 403 from verify_admin), clears it and
 * calls the handler AdminDashboard registers (redirect to the admin login).
 */
export const adminApi = axios.create({ baseURL: API });

let authLostHandler = null;
export function setAuthLostHandler(fn) {
  authLostHandler = fn;
}

const readToken = () => {
  try {
    return localStorage.getItem('admin_token');
  } catch {
    return null;
  }
};

adminApi.interceptors.request.use((config) => {
  const token = readToken();
  if (token) config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    if (status === 401 || status === 403) {
      try {
        localStorage.removeItem('admin_token');
      } catch {
        /* storage blocked: the redirect still ends the session */
      }
      authLostHandler?.();
    }
    return Promise.reject(err);
  }
);

/**
 * Load one admin resource with explicit loading / error states.
 *   const { data, error, loading, reload, setData } = useAdminQuery(() => adminApi.get(...).then(r => r.data), [dep]);
 * A response that arrives after a newer request started is dropped, so a slow
 * earlier fetch can never overwrite fresher data (or another campaign's IOCs).
 * `loading` is only true until the first answer; reloads keep the old data on screen.
 */
export function useAdminQuery(fetcher, deps = [], { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: enabled });
  const seq = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(async () => {
    const id = ++seq.current;
    setState((s) => ({ ...s, loading: s.data == null, error: null }));
    try {
      const data = await fetcherRef.current();
      if (id === seq.current) setState({ data, error: null, loading: false });
    } catch (error) {
      if (id === seq.current) setState((s) => ({ ...s, error, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setState({ data: null, error: null, loading: true });
    reload();
  }, [enabled, reload, ...deps]);

  const setData = useCallback((update) => {
    setState((s) => ({ ...s, data: typeof update === 'function' ? update(s.data) : update }));
  }, []);

  return { ...state, reload, setData };
}

/**
 * In-flight tracking for mutating buttons. `run(key, fn)` ignores a second
 * call while the first with the same key is pending (no double submit);
 * `busy(key)` drives the disabled state and the "...ing" label.
 */
export function useBusy() {
  const [pending, setPending] = useState({});
  const ref = useRef({});
  const run = useCallback(async (key, fn) => {
    if (ref.current[key]) return undefined;
    ref.current[key] = true;
    setPending((p) => ({ ...p, [key]: true }));
    try {
      return await fn();
    } finally {
      delete ref.current[key];
      setPending((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  }, []);
  const busy = useCallback((key) => Boolean(pending[key]), [pending]);
  return { run, busy, any: Object.keys(pending).length > 0 };
}

export function toastError(err, fallback) {
  toast.error(fallback, { description: describeError(err) || undefined });
}

/* ── Formatting ─────────────────────────────────────────────── */

const pad = (n) => String(n).padStart(2, '0');

/** "2026-10-15 19:14 UTC": the same clock the status strip shows. */
export function formatUtc(iso, { seconds = false } = {}) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const time = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}${seconds ? `:${pad(d.getUTCSeconds())}` : ''}`;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${time} UTC`;
}

export function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/* ── IOC typing ─────────────────────────────────────────────── */

export const IOC_TYPES = [
  { value: 'ip', label: 'IP' },
  { value: 'domain', label: 'Domain' },
  { value: 'url', label: 'URL' },
  { value: 'hash', label: 'Hash' },
  { value: 'email', label: 'Email' },
];
export const iocTypeLabel = (t) => IOC_TYPES.find((x) => x.value === t)?.label || String(t || 'Unknown');

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}(:\d{1,5})?$/;
const IPV6 = /^[0-9a-f:]+$/i;
const DOMAIN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}\.?$/i;
const URL_RE = /^(https?|hxxps?|ftp):\/\/\S+$/i;
const HASH = /^[a-f0-9]{32}$|^[a-f0-9]{40}$|^[a-f0-9]{64}$|^[a-f0-9]{128}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,24}$/i;

/** Strip the quotes and defanging that article extraction leaves around values. */
export function cleanIocValue(value) {
  return String(value || '')
    .trim()
    .replace(/^["'`<]+|["'`>,;]+$/g, '')
    .replace(/\[\.\]|\(\.\)/g, '.')
    .trim();
}

/** Best guess of the type for a typed value, or null when it fits none. */
export function inferIocType(value) {
  const v = cleanIocValue(value);
  if (!v) return null;
  if (URL_RE.test(v)) return 'url';
  if (EMAIL.test(v)) return 'email';
  if (IPV4.test(v) || (v.includes(':') && v.split(':').length > 2 && IPV6.test(v))) return 'ip';
  if (HASH.test(v)) return 'hash';
  if (DOMAIN.test(v)) return 'domain';
  return null;
}

/** Why a value does not look like its declared type (null when it does). */
export function iocProblem(type, value) {
  const v = cleanIocValue(value);
  if (!v) return 'Empty value';
  if (/\s/.test(v)) return 'Contains spaces: likely a name or phrase, not an indicator';
  const guess = inferIocType(v);
  if (!guess) return `Does not look like a valid ${iocTypeLabel(type).toLowerCase()}`;
  if (type && guess !== type) return `Looks like ${/^[aeiou]/i.test(iocTypeLabel(guess)) ? 'an' : 'a'} ${iocTypeLabel(guess)}, typed as ${iocTypeLabel(type)}`;
  return null;
}

export async function copyText(text, what = 'Value') {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error(`Could not copy ${what.toLowerCase()}`, { description: 'Clipboard access was blocked by the browser.' });
  }
}

/* Same list the registration form offers. Insights targeted to "Global" are
   shown to every industry, exactly like untargeted ones. */
export const INDUSTRIES = ['Finance', 'Healthcare', 'Technology', 'Government', 'Energy', 'Retail', 'Manufacturing', 'Education'];

/** Save rows as a CSV file (RFC 4180 quoting, UTF-8 with BOM so Excel reads it). */
export function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿', body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
